import { FormGroup, Text } from '@ksyos/design-system';
import { type JSX, useEffect, useState } from 'react';
import styled from 'styled-components';
import type { IProcessActivity } from '@common/interfaces';
import { type CustomControlCode } from '@common/enums';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { getActivityPerformer } from '@common/utils/processUtil';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { ProcessContext } from '../../carePath/process/processContext';

type AdditionalQuestionReadProps = Readonly<{
    customControlCode: CustomControlCode;
    message?: string | null;
}>;

export function AdditionalQuestionRead({
    customControlCode,
    message,
}: AdditionalQuestionReadProps): JSX.Element | null {
    const [practitioner, setPractitioner] = useState<IProcessActivity['performerPractitioner']>();
    const { process } = useDefinedContext(ProcessContext);

    useEffect(() => {
        const { performerPractitioner } = getActivityPerformer(process, customControlCode);
        setPractitioner(performerPractitioner);
    }, []);

    if (!message) {
        return null;
    }

    return (
        <FormGroup
            label={getDisplayName({ nameFamily: practitioner?.nameFamily, nameGiven: practitioner?.nameGiven })}
            labelAvatar={{
                initials: getDisplayInitials({
                    nameFamily: practitioner?.nameFamily,
                    nameGiven: practitioner?.nameGiven,
                }),
            }}
        >
            <StyledFormParagraph size="sm">{nonNullable(message)}</StyledFormParagraph>
        </FormGroup>
    );
}

const StyledFormParagraph = styled(Text)`
    color: ${({ theme }) => theme.colors.black};
    background-color: ${({ theme }) => theme.colors.neutral100};
    height: ${({ theme }) => theme.space[12]};
    display: flex;
    align-items: center;
    border-radius: ${({ theme }) => theme.radii.base};
    padding: ${({ theme }) => theme.space[4]};
`;
